import React, { useRef, useEffect, useCallback } from 'react';
import { animate } from 'motion/react';
import { cn } from '../../lib/utils';

/**
 * Card component with optional animated glowing border effect
 *
 * @param {Object} props
 * @param {string} props.title - Card title (optional)
 * @param {React.Component|React.ElementType} props.icon - Icon component (e.g., from react-icons)
 * @param {React.ReactNode} props.image - Image element to render before content (optional)
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Card content
 * @param {boolean} props.animatedBorder - Enable animated border effect (default: true)
 * @param {number} props.blur - Blur amount in pixels (default: 0)
 * @param {number} props.inactiveZone - Zone around center where border is inactive (0-1, default: 0.7)
 * @param {number} props.proximity - Distance from card where border activates (default: 0)
 * @param {number} props.spread - Spread angle of border animation in degrees (default: 20)
 * @param {number} props.movementDuration - Duration of border movement animation in seconds (default: 2)
 * @param {number} props.borderWidth - Width of the animated border in pixels (default: 1)
 * @param {string} props.variant - Border color variant: 'default' or 'white' (default: 'default')
 * @param {boolean} props.glow - Enable glow effect (default: false)
 */
const Card = ({
  title,
  icon: Icon,
  image,
  className = '',
  children,
  animatedBorder = true,
  blur = 0,
  inactiveZone = 0,
  proximity = 64,
  spread = 40,
  movementDuration = 2,
  borderWidth = 3,
  variant = 'default',
  glow = true,
}) => {
  const containerRef = useRef(null);
  const lastPosition = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef(0);

  const handleMove = useCallback(
    (e) => {
      if (!containerRef.current) return;

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        const element = containerRef.current;
        if (!element) return;

        const { left, top, width, height } = element.getBoundingClientRect();
        const mouseX = e?.x ?? lastPosition.current.x;
        const mouseY = e?.y ?? lastPosition.current.y;

        if (e) {
          lastPosition.current = { x: mouseX, y: mouseY };
        }

        const center = [left + width * 0.5, top + height * 0.5];
        const distanceFromCenter = Math.hypot(
          mouseX - center[0],
          mouseY - center[1]
        );
        const inactiveRadius = 0.5 * Math.min(width, height) * inactiveZone;

        if (distanceFromCenter < inactiveRadius) {
          element.style.setProperty('--active', '0');
          return;
        }

        const isActive =
          mouseX > left - proximity &&
          mouseX < left + width + proximity &&
          mouseY > top - proximity &&
          mouseY < top + height + proximity;

        element.style.setProperty('--active', isActive ? '1' : '0');

        if (!isActive) return;

        const currentAngle =
          parseFloat(element.style.getPropertyValue('--start')) || 0;
        let targetAngle =
          (180 * Math.atan2(mouseY - center[1], mouseX - center[0])) / Math.PI +
          90;

        const angleDiff = ((targetAngle - currentAngle + 180) % 360) - 180;
        const newAngle = currentAngle + angleDiff;

        animate(currentAngle, newAngle, {
          duration: movementDuration,
          ease: [0.16, 1, 0.3, 1],
          onUpdate: (value) => {
            element.style.setProperty('--start', String(value));
          },
        });
      });
    },
    [inactiveZone, proximity, movementDuration]
  );

  useEffect(() => {
    if (!animatedBorder) return;

    const handleScroll = () => handleMove();
    const handlePointerMove = (e) => handleMove(e);

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.body.addEventListener('pointermove', handlePointerMove, {
      passive: true,
    });

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener('scroll', handleScroll);
      document.body.removeEventListener('pointermove', handlePointerMove);
    };
  }, [handleMove, animatedBorder]);

  // Gradient colors from design system
  // Using design system colors: pink (#e50069), tertiary-alt (#b400c1), primary (#009bf7), secondary (#ff3451)
  const gradient =
    variant === 'white'
      ? `repeating-conic-gradient(
          from 236.84deg at 50% 50%,
          var(--color-black),
          var(--color-black) calc(25% / var(--repeating-conic-gradient-times))
        )`
      : `radial-gradient(circle, #e50069 10%, #e5006900 20%),
        radial-gradient(circle at 40% 40%, #b400c1 5%, #b400c100 15%),
        radial-gradient(circle at 60% 60%, #009bf7 10%, #009bf700 20%), 
        radial-gradient(circle at 40% 60%, #ff3451 10%, #ff345100 20%),
        repeating-conic-gradient(
          from 236.84deg at 50% 50%,
          #e50069 0%,
          #b400c1 calc(25% / var(--repeating-conic-gradient-times)),
          #009bf7 calc(50% / var(--repeating-conic-gradient-times)), 
          #ff3451 calc(75% / var(--repeating-conic-gradient-times)),
          #e50069 calc(100% / var(--repeating-conic-gradient-times))
        )`;

  return (
    <div
      className={cn(
        'p-6 bg-[rgba(19,19,19,0.4)] h-100 w-100 border !border-[rgb(39,39,42)] rounded-[1.25rem]',
        'card-no-hover-transform relative',
        className
      )}
    >
      <div className="rounded-[1.25rem] text-left border !border-[rgb(39,39,42)] bg-[#060606] service service__style--1 shadow-[rgba(0,0,0,0)_0px_0px_0px_0px,rgba(0,0,0,0)_0px_0px_0px_0px,rgba(45,45,45,0.3)_0px_0px_27px_0px] h-full">
        {image}

        {Icon && (
          <div className="icon">
            <Icon />
          </div>
        )}

        <div className="content">
          {title && <h4 className="title w-600">{title}</h4>}
          {children}
        </div>
      </div>

      {/* Border div - shown when animatedBorder is false */}
      <div
        className={cn(
          'pointer-events-none absolute -inset-px hidden rounded-[inherit] border opacity-0 transition-opacity',
          glow && 'opacity-100',
          variant === 'white' && 'border-white',
          !animatedBorder && '!block'
        )}
      />
      {/* Glow effect div - hidden when animatedBorder is false */}
      <div
        ref={animatedBorder ? containerRef : null}
        style={
          animatedBorder
            ? {
                '--blur': `${blur}px`,
                '--spread': spread,
                '--start': '0',
                '--active': '0',
                '--glowingeffect-border-width': `${borderWidth}px`,
                '--repeating-conic-gradient-times': '5',
                '--gradient': gradient,
              }
            : undefined
        }
        className={cn(
          'pointer-events-none absolute inset-0 rounded-[inherit] opacity-100 transition-opacity',
          glow && 'opacity-100',
          blur > 0 && 'blur-[var(--blur)]',
          !animatedBorder && '!hidden'
        )}
      >
        <div
          className={cn(
            'glow',
            'rounded-[inherit]',
            'after:content-[""] after:rounded-[inherit] after:absolute after:inset-[calc(-1*var(--glowingeffect-border-width))]',
            'after:[border:var(--glowingeffect-border-width)_solid_transparent]',
            'after:[background:var(--gradient)] after:[background-attachment:fixed]',
            'after:opacity-[var(--active)] after:transition-opacity after:duration-300',
            'after:[mask-clip:padding-box,border-box]',
            'after:[mask-composite:intersect]',
            'after:[mask-image:linear-gradient(#0000,#0000),conic-gradient(from_calc((var(--start)-var(--spread))*1deg),#00000000_0deg,#fff,#00000000_calc(var(--spread)*2deg))]'
          )}
        />
      </div>
    </div>
  );
};

export default Card;
